/**
 * Centralized formatting utilities for Brazilian locale (pt-BR).
 * Currency (BRL), dates (DD/MM/YYYY), plates, phones.
 */

// ─── Currency ───────────────────────────────────────────────

const currencyFormatter = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

/** Format number to BRL currency: R$ 1.234,56 */
export function formatCurrency(value: number | string): string {
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return "R$ 0,00";
  return currencyFormatter.format(num);
}

/** Parse BRL input string "1.234,56" → 1234.56 */
export function parseBRLInput(value: string): number {
  if (!value) return 0;
  const cleaned = value.replace(/[R$\s]/g, "").replace(/\./g, "").replace(",", ".");
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
}

/** Convert number 1234.56 → display string "1.234,56" (without R$ prefix) */
export function toBRLInput(value: number): string {
  if (!value && value !== 0) return "";
  return value.toFixed(2).replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

// ─── Dates ──────────────────────────────────────────────────

const dateShortFormatter = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

const dateLongFormatter = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "long",
  year: "numeric",
});

const dateTimeFormatter = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

const dateMonthShortFormatter = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "short",
});

function toDate(date: string | Date): Date {
  if (date instanceof Date) return date;
  return new Date(date);
}

/** Format to DD/MM/YYYY: 15/01/2025 */
export function formatDateShort(date: string | Date): string {
  return dateShortFormatter.format(toDate(date));
}

/** Format to long: 15 de janeiro de 2025 */
export function formatDateLong(date: string | Date): string {
  return dateLongFormatter.format(toDate(date));
}

/** Format to DD/MM/YYYY HH:mm: 15/01/2025 14:30 */
export function formatDateTime(date: string | Date): string {
  return dateTimeFormatter.format(toDate(date));
}

/** Format to DD mon: 15 jan. (for table columns) */
export function formatDateMonthShort(date: string | Date): string {
  return dateMonthShortFormatter.format(toDate(date));
}

/** Parse DD/MM/YYYY string → Date, or null if invalid */
export function parseDateInput(value: string): Date | null {
  if (!value) return null;
  const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return null;
  const [, day, month, year] = match;
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  if (isNaN(date.getTime())) return null;
  if (date.getDate() !== parseInt(day)) return null; // invalid day (e.g. 31/02)
  return date;
}

/** Convert Date → DD/MM/YYYY string for input fields */
export function toDateInput(date: Date): string {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

/** Convert ISO string or Date to YYYY-MM-DD (for HTML date inputs) */
export function toISODateInput(date: string | Date): string {
  const d = toDate(date);
  return d.toISOString().split("T")[0];
}

// ─── Plates ─────────────────────────────────────────────────

/** Format vehicle plate: ABC-1234 (old) or ABC1D23 (Mercosul) */
export function formatPlate(plate?: string): string {
  if (!plate) return "—";
  const clean = plate.replace(/[^A-Z0-9]/gi, "").toUpperCase();
  if (clean.length === 7) {
    // Mercosul: 4th char is a letter → keep as-is
    if (/[A-Z]/.test(clean[4])) {
      return clean;
    }
    // Old format: ABC-1234
    return `${clean.slice(0, 3)}-${clean.slice(3)}`;
  }
  return clean;
}

// ─── Phone ──────────────────────────────────────────────────

/** Format phone: (54) 99999-9999 */
export function formatPhone(phone?: string): string {
  if (!phone) return "";
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return phone;
}

/** Extract digits and add +55 country code for WhatsApp */
export function toWhatsAppPhone(phone: string): string {
  const digits = phone.replace(/\D/g, "");
  if (digits.length <= 11) {
    return `55${digits}`;
  }
  return digits;
}
